import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	RefObject,
	useEffect,
	useRef
} from "react";
import { HubItemCategoryNodeFormModel, HubItemGroupNodeFormModel } from "./common";
import {
	IconButton,
	ReorderableList
} from "client/Components";
import { HubSidebarItemContent } from "./HubSidebarItemContent";
import { Guid } from "client_legacy/ndaw/guid";
import { getClientId } from "client/Misc/clientId";
import { CategoryItemMenu } from "./CategoryItemMenu";
import { useItemMenuState } from "client/Hooks";
import { ensureNotNil, generateTrackingId } from "client/Misc/utils";

export type HubSidebarCategoryItemProps = {
	attachable: IValueInputAttachable<HubItemCategoryNodeFormModel>;
	groupsHierarchy: readonly HubItemGroupNodeFormModel[];
	containingGroupId?: Guid;
	nodeClientIdToScrollTo: Guid | null;
	onNodeClientIdToScrollChange: (id: Guid | null) => void;
	onEdit(clientId: Guid): void;
	onDelete(clientId: Guid): void;
	onMoveToGroup(clientId: Guid, targetGroupId: Guid): void;
	onMoveToTop?(clientId: Guid): void;
};

export const HubSidebarCategoryItem = (props: HubSidebarCategoryItemProps): ReactElement => {
	const {
		attachable,
		containingGroupId,
		groupsHierarchy,
		nodeClientIdToScrollTo,
		onNodeClientIdToScrollChange,
		onEdit,
		onMoveToGroup,
		onMoveToTop,
		onDelete
	} = props;

	const {
		name
	} = attachable.value;

	const itemRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const menuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);
	const clientId: Guid = getClientId(attachable.value);

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	useEffect(
		(): void => {
			if (nodeClientIdToScrollTo === clientId) {
				itemRef.current
					?.scrollIntoView();

				onNodeClientIdToScrollChange(null);
			}
		},
		[
			nodeClientIdToScrollTo,
			clientId,
			onNodeClientIdToScrollChange
		]
	);

	return (
		<>
			<ReorderableList.RowItem
				trackingId="category-item"
				actions={(
					<IconButton
						ref={menuButtonRef}
						icon="ellipsis"
						kind="transparent"
						shape="round"
						trackingId="open-actions"
						onClick={() => onOpenMoreMenu()}
						isActive={menuState.isOpen && !menuState.isContextMenu}
					/>
				)}
				mouseInteraction={{
					onContextMenu: onOpenContextMenu
				}}
				isActive={menuState.isOpen}
			>
				<div ref={itemRef}>
					<HubSidebarItemContent
						trackingId="content"
						icon={{
							kind: "font",
							name: "note-sticky",
							color: "primary"
						}}
					>
						{name}
					</HubSidebarItemContent>
				</div>
			</ReorderableList.RowItem>
			{menuState.isOpen && (
				<CategoryItemMenu
					target={ensureNotNil(menuButtonRef.current)}
					onClose={onCloseMenu}
					menuState={menuState}
					containingGroupId={containingGroupId}
					groupsHierarchy={groupsHierarchy}
					onEdit={() => onEdit(clientId)}
					onMoveToGroup={(targetGroupId) => onMoveToGroup(clientId, targetGroupId)}
					onDelete={() => onDelete(clientId)}
					onMoveToTop={onMoveToTop
						? () => onMoveToTop(clientId)
						: undefined
					}
					trackingId={generateTrackingId("open-actions", "__dropover")}
				/>
			)}
		</>
	);
};
HubSidebarCategoryItem.displayName = "HubSidebarCategoryItem";